import { useRef, useCallback, useEffect, useState } from 'react';
import type { Element } from '@/components/canvas/document/publicTypes';
import type { Block } from '@/components/canvas/blocks/types';
import type { ComputedGridGuides } from '@/components/canvas/workspace/overlays/gridGuides';
import {
  computeDragSnap,
  type AlignmentGuideLine,
} from '@/components/canvas/transform/alignmentGuides';

export type ElementDragSource = 'page' | 'workspace';

export interface ElementDragState {
  startClientX: number;
  startClientY: number;
  startPositions: { id: string; x: number; y: number }[];
  source: ElementDragSource;
  sourcePageId?: string;
}

export interface ElementDragMovePayload {
  dx: number;
  dy: number;
  rawDx: number;
  rawDy: number;
  startPositions: { id: string; x: number; y: number }[];
  source: ElementDragSource;
  sourcePageId?: string;
}

export interface PageSnapContext {
  elements: Element[];
  blocks: Block[];
  guides: ComputedGridGuides;
  pageWidth: number;
  pageHeight: number;
  zoom: number;
}

export interface UseElementDragCallbacks {
  onDragMove?: (payload: ElementDragMovePayload) => void;
  onDragEnd?: (payload: ElementDragMovePayload) => void;
  /** 未产生位移就松手时调用，用于清除 pushHistory 留下的 _pendingBase */
  onDragCancel?: () => void;
}

export function useElementDrag(
  zoom: number,
  pageSnapRef: React.MutableRefObject<PageSnapContext>,
  callbacks: UseElementDragCallbacks,
) {
  const [elementDrag, setElementDrag] = useState<ElementDragState | null>(null);
  const [dragOffset, setDragOffset] = useState({ dx: 0, dy: 0 });
  const [alignmentGuides, setAlignmentGuides] = useState<AlignmentGuideLine[]>([]);
  const dragOffsetRef = useRef({ dx: 0, dy: 0 });
  dragOffsetRef.current = dragOffset;
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;

  const startDrag = useCallback(
    (
      startClientX: number,
      startClientY: number,
      startPositions: { id: string; x: number; y: number }[],
      meta: { source: ElementDragSource; sourcePageId?: string },
    ) => {
      setElementDrag({ startClientX, startClientY, startPositions, ...meta });
      setDragOffset({ dx: 0, dy: 0 });
      setAlignmentGuides([]);
    },
    [],
  );

  useEffect(() => {
    if (!elementDrag) return;
    const { startClientX, startClientY, startPositions, source, sourcePageId } = elementDrag;

    const buildPayload = (rawDx: number, rawDy: number, dx: number, dy: number): ElementDragMovePayload => ({
      dx,
      dy,
      rawDx,
      rawDy,
      startPositions,
      source,
      sourcePageId,
    });

    const applySnap = (rawDx: number, rawDy: number) => {
      if (source === 'page') {
        const ctx = pageSnapRef.current;
        const snapped = computeDragSnap({
          startPositions,
          rawDx,
          rawDy,
          elements: ctx.elements,
          blocks: ctx.blocks,
          pageWidth: ctx.pageWidth,
          pageHeight: ctx.pageHeight,
          gridGuides: ctx.guides,
          zoom: ctx.zoom,
        });
        setDragOffset({ dx: snapped.dx, dy: snapped.dy });
        setAlignmentGuides(snapped.guides);
        return buildPayload(rawDx, rawDy, snapped.dx, snapped.dy);
      }
      setDragOffset({ dx: rawDx, dy: rawDy });
      setAlignmentGuides([]);
      return buildPayload(rawDx, rawDy, rawDx, rawDy);
    };

    const onMove = (e: MouseEvent) => {
      const rawDx = (e.clientX - startClientX) / zoom;
      const rawDy = (e.clientY - startClientY) / zoom;
      const payload = applySnap(rawDx, rawDy);
      callbacksRef.current.onDragMove?.(payload);
    };

    const onUp = () => {
      const { dx, dy } = dragOffsetRef.current;
      if (startPositions.length > 0 && (dx !== 0 || dy !== 0)) {
        callbacksRef.current.onDragEnd?.(buildPayload(dx, dy, dx, dy));
      } else {
        callbacksRef.current.onDragCancel?.();
      }
      setElementDrag(null);
      setDragOffset({ dx: 0, dy: 0 });
      setAlignmentGuides([]);
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [elementDrag, zoom, pageSnapRef]);

  return {
    elementDrag,
    setElementDrag,
    dragOffset,
    setDragOffset,
    startDrag,
    alignmentGuides,
  };
}
